import {
  ReadonlyWatchable,
  TargetableModel,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import {
  Desc,
  Asc,
  Relation,
  RelationRef,
  Expression,
  And,
  Constant,
} from '@cotera/era';
import { Filter, Sort } from './types';
import { CellComponentProps, Column } from '../components/data-grid';
import { StorageConnection } from '../models/user-branches';
import { DuckDBQueryResult } from '@cotera/client/app/etc/duckdb';
import { CacheResultData } from '@cotera/client/app/etc/data/artifacts';

export interface DataGridModelParams {
  readonly rel: Relation;
  readonly baseRel: Relation;
  readonly id: string;
  readonly storage: StorageConnection;
  readonly columns: Watchable<Column[]>;
  readonly artifactMeta?: CacheResultData | null;
  readonly renderer?: {
    cell?: React.ComponentType<
      CellComponentProps<any> & {
        model: DataGridModel;
        rel: Relation;
      }
    >;
    detailViewCell?: React.ForwardRefExoticComponent<
      {
        column: string;
        value: any;
        rel: Relation;
        focused: boolean;
      } & React.RefAttributes<HTMLDivElement>
    >;
  };
  readonly managedProps?: {
    isTargeted?: boolean;
    filters?: Watchable<Filter[]>;
  };
}

export class DataGridModel implements TargetableModel {
  readonly id: string;
  readonly rel: Relation;
  //the original rel that was used to create the data grid - i.e. the one that points to the warehouse table
  readonly baseRel: Relation;
  readonly storage: StorageConnection;
  readonly artifactMeta: CacheResultData | null;
  readonly managedProps?: {
    isTargeted?: boolean;
    filters?: Watchable<Filter[]>;
  };
  readonly renderer?: {
    cell?: React.ComponentType<
      CellComponentProps<any> & {
        model: DataGridModel;
        rel: Relation;
      }
    >;
    detailViewCell?: React.ForwardRefExoticComponent<
      {
        column: string;
        value: any;
        rel: Relation;
        focused: boolean;
      } & React.RefAttributes<HTMLDivElement>
    >;
  };
  readonly sort: Watchable<Sort[]>;
  readonly dataRel: ReadonlyWatchable<Relation>;
  readonly columns: Watchable<Column[]>;
  readonly headersExpanded = Watchable.fromValue<boolean | null>(null);
  readonly headersHaveBeenExpanded: ReadonlyWatchable<boolean>;
  readonly focusedCell = Watchable.fromValue<{
    row: number;
    col: number;
    columnKey: string;
    rowData: Record<string, any>;
  } | null>(null);
  readonly selectedRows = Watchable.fromValue<Set<number>>(new Set());
  readonly selectionAnchor = Watchable.fromValue<number | null>(null);
  readonly totalLoadedRows = Watchable.fromValue<number>(0);
  readonly totalRows = Watchable.fromValue<number | null>(null);
  readonly data = Watchable.fromValue<DuckDBQueryResult | null>(null);

  private _isTargeted: boolean;
  readonly filters: Watchable<Filter[]> = Watchable.fromValue<Filter[]>([]);
  readonly headerHeight: ReadonlyWatchable<number>;
  readonly cacheKey: ReadonlyWatchable<string>;

  constructor(params: DataGridModelParams) {
    this.id = params.id;
    this.rel = params.rel;
    this.storage = params.storage;
    this.artifactMeta = params.artifactMeta ?? null;
    this.managedProps = params.managedProps;
    this.renderer = params.renderer;
    this.sort = Watchable.fromValue<Sort[]>([]);
    this.filters =
      params.managedProps?.filters ?? Watchable.fromValue<Filter[]>([]);
    this.dataRel = Watchable.from((get) => {
      const sort = get(this.sort);
      const filters = get(this.managedProps?.filters ?? this.filters);

      return makeArtifactRel(params.rel, sort, filters);
    });
    this.cacheKey = Watchable.from((get) => {
      return get(this.dataRel).eraqlHash();
    });
    this._isTargeted = params.managedProps?.isTargeted ?? false;
    this.columns = params.columns;
    this.headersHaveBeenExpanded = Watchable.from((get) => {
      const headersExpanded = get(this.headersExpanded);
      return headersExpanded !== null;
    });
    this.headerHeight = Watchable.from((get) => {
      const headersExpanded = get(this.headersExpanded);
      return headersExpanded ? 84 : 36;
    });
    this.baseRel = params.baseRel;
  }

  get isTargeted() {
    return this.managedProps?.isTargeted ?? this._isTargeted;
  }

  target() {
    if (this.managedProps?.isTargeted !== undefined) {
      return;
    }

    this._isTargeted = true;
  }

  markAsNotTargeted() {
    if (this.managedProps?.isTargeted !== undefined) {
      return;
    }

    this._isTargeted = false;
  }
}

const makeArtifactRel = (rel: Relation, sort: Sort[], filters: Filter[]) => {
  const filterExpr = (t: RelationRef): Expression[] => {
    if (filters.length === 0) {
      return [];
    }

    return filters
      .map((x) => {
        if (x.filter.t === 'category') {
          const hasNull = x.filter.value.includes(null);
          const nonNullFilters = x.filter.value
            .filter((v) => v !== null)
            .map((v) => Constant(v).cast('string'));

          return [
            nonNullFilters.length > 0
              ? t.attr(x.column).cast('string').oneOf(nonNullFilters)
              : undefined,
            hasNull ? t.attr(x.column).isNull() : undefined,
          ];
        } else {
          //must be continuous
          const min = Math.min(...x.filter.value.map((v) => v.min));
          const max = Math.max(...x.filter.value.map((v) => v.max));
          return [t.attr(x.column).between(min, max)];
        }
      })
      .flat()
      .filter((x) => x !== undefined);
  };

  const sortExpr = (t: RelationRef) =>
    sort.map((x) => {
      const fn = x.direction === 'asc' ? Asc : Desc;

      return fn(t.attr(x.column));
    });

  const makeWhereClause = (t: RelationRef) => {
    const filterExprs = filterExpr(t);

    if (filterExprs.length === 0) {
      return Constant(true);
    }

    if (filterExprs.length === 1) {
      return filterExprs[0]!;
    }

    return And(...filterExprs);
  };

  if (sort.length > 0) {
    return rel.where((t) => makeWhereClause(t)).orderBy((t) => sortExpr(t));
  }

  return rel.where((t) => makeWhereClause(t));
};
